import React from 'react';

import { CodeBlock } from '@trqk/components';
import { locale } from '@trqk/utils';

/**
 * Toast message shown when one or more file uploads failed
 * @param props - {@link ToastRenderContentProps}
 * @returns JSX
 */
export const SomeUploadsFailed = ({
    input,
}: ToastRenderContentProps<{
    groupStoppedInput: OnGroupStoppedInput<FilenameInput, UploadStatusOutput>;
    /**
     * A file descriptor like 'audio file' or 'detection file'
     */
    fileDescriptor: string;
}>): JSX.Element => {
    const error = input?.groupStoppedInput?.error ?? [];
    const hasAtLeastOneError =
        typeof input?.groupStoppedInput?.error?.[0]?.error === 'string';
    const success = input?.groupStoppedInput?.success ?? [];
    const fileDescriptor = input?.fileDescriptor ?? 'file';

    let descriptor = `${locale.enUS.TOASTS.SOME_UPLOADS_FAILED.YOUR} ${fileDescriptor} ${locale.enUS.TOASTS.SOME_UPLOADS_FAILED.UPLOAD}`;

    if (error.length > 1 || success.length !== 0) {
        descriptor = `${error.length}  ${locale.enUS.TOASTS.SOME_UPLOADS_FAILED.OF_YOUR} ${fileDescriptor} ${locale.enUS.TOASTS.SOME_UPLOADS_FAILED.UPLOADS}`;
    }

    const warning = `${locale.enUS.TOASTS.SOME_UPLOADS_FAILED.THERE_WAS_A_PROBLEM} ${descriptor}.`;

    return (
        <div style={{ width: 410 }} className="px-2">
            <div className="font-bold text-highlight-orange tracking-wider mb-2">
                {locale.enUS.TOASTS.SOME_UPLOADS_FAILED.UPLOAD_ISSUE}
            </div>
            {!hasAtLeastOneError && (
                <>
                    <div className="mb-2">{warning}</div>
                </>
            )}
            {hasAtLeastOneError && (
                <>
                    <div className="mb-2">{warning}</div>
                    <div
                        className="overflow-y-auto mt-2"
                        style={{ maxHeight: 400 }}
                    >
                        {error.map(
                            (task: UploadFileTask): JSX.Element | null => {
                                if (!task.error) {
                                    return null;
                                }

                                return (
                                    <div key={task.taskId} className="mt-2">
                                        <hr />
                                        {!task.input?.filename && (
                                            <div className="font-semibold mt-1">
                                                {
                                                    locale.enUS.TOASTS
                                                        .SOME_UPLOADS_FAILED
                                                        .ERROR_MESSAGE
                                                }
                                            </div>
                                        )}
                                        {task.input?.filename && (
                                            <div className="mt-2 break-all">
                                                <span className="font-semibold">
                                                    {
                                                        locale.enUS.TOASTS
                                                            .SOME_UPLOADS_FAILED
                                                            .INFO_FOR
                                                    }
                                                </span>
                                                <span className="break-all">{`: ${task.input?.filename}`}</span>
                                            </div>
                                        )}
                                        <CodeBlock
                                            code={task.error}
                                            displayMode="light"
                                            showControls={false}
                                        />
                                    </div>
                                );
                            },
                        )}
                    </div>
                </>
            )}
        </div>
    );
};
